import React, { useState, useEffect } from 'react';
import { 
  BookOpen, 
  Plus, 
  PlayCircle, 
  FileText, 
  Headphones, 
  Video, 
  Award, 
  Lock, 
  CheckCircle, 
  Star,
  Crown,
  Filter,
  Search,
  TrendingUp,
  Download,
  Users,
  Trophy,
  Zap,
  Settings,
  BarChart3,
  Edit,
  Trash2,
  Eye
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const AdminTrainingVault = () => {
  const { user } = useAuth();
  const [trainingStats, setTrainingStats] = useState({});
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [seedingContent, setSeedingContent] = useState(false);

  // Content type configurations
  const contentTypeConfig = {
    ebook: { 
      icon: BookOpen, 
      color: 'blue', 
      label: 'eBook',
      bgClass: 'bg-blue-50 border-blue-200',
      textClass: 'text-blue-800',
      iconClass: 'text-blue-600'
    },
    podcast: { 
      icon: Headphones, 
      color: 'purple', 
      label: 'Podcast',
      bgClass: 'bg-purple-50 border-purple-200',
      textClass: 'text-purple-800',
      iconClass: 'text-purple-600'
    },
    video_course: { 
      icon: Video, 
      color: 'red', 
      label: 'Video Course',
      bgClass: 'bg-red-50 border-red-200',
      textClass: 'text-red-800',
      iconClass: 'text-red-600'
    },
    ad_copy: { 
      icon: FileText, 
      color: 'green', 
      label: 'Ad Copy',
      bgClass: 'bg-green-50 border-green-200',
      textClass: 'text-green-800',
      iconClass: 'text-green-600'
    },
    playbook: { 
      icon: BookOpen, 
      color: 'yellow', 
      label: 'Playbook',
      bgClass: 'bg-yellow-50 border-yellow-200',
      textClass: 'text-yellow-800',
      iconClass: 'text-yellow-600'
    },
    training_module: { 
      icon: PlayCircle, 
      color: 'indigo', 
      label: 'Training',
      bgClass: 'bg-indigo-50 border-indigo-200',
      textClass: 'text-indigo-800',
      iconClass: 'text-indigo-600'
    }
  };

  // Access level configurations
  const accessLevelConfig = {
    free: { label: 'Free', color: '#6B7280', icon: Users },
    boot_camp: { label: 'Boot Camp', color: '#F59E0B', icon: Award },
    rookie_pro: { label: 'Rookie Pro', color: '#10B981', icon: Star },
    pro: { label: 'Pro', color: '#3B82F6', icon: TrendingUp },
    enterprise: { label: 'Enterprise', color: '#8B5CF6', icon: Crown }
  };

  const fetchTrainingStats = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_BACKEND_URL;
      const token = localStorage.getItem('auth_token');
      
      const response = await fetch(`${backendUrl}/api/admin/training-vault/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTrainingStats(data);
      } else {
        toast.error('Failed to load training statistics');
      }
    } catch (error) {
      console.error('Error fetching training stats:', error);
      toast.error('Error loading training statistics');
    }
  };

  const seedTrainingVault = async () => {
    setSeedingContent(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_BACKEND_URL;
      const token = localStorage.getItem('auth_token');
      
      const response = await fetch(`${backendUrl}/api/admin/training-vault/seed`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        await fetchTrainingStats(); // Refresh stats
      } else {
        toast.error('Failed to seed training vault');
      }
    } catch (error) {
      console.error('Error seeding training vault:', error);
      toast.error('Error seeding training vault');
    } finally {
      setSeedingContent(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchTrainingStats();
      setLoading(false);
    };
    loadData();
  }, []);

  const renderStatsCard = (title, value, IconComponent, color, description) => (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <p className="text-2xl font-bold text-gray-900">{value}</p>
            {description && <p className="text-xs text-gray-500 mt-1">{description}</p>}
          </div>
          <div className={`p-3 rounded-full bg-${color}-100`}>
            <IconComponent className={`h-6 w-6 text-${color}-600`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const renderContentTypeBreakdown = () => {
    if (!trainingStats.content_by_type) return null;
    
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Content by Type
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Object.entries(trainingStats.content_by_type).map(([type, count]) => {
              const config = contentTypeConfig[type] || contentTypeConfig.training_module;
              const IconComponent = config.icon;
              const total = trainingStats.total_content || 1;
              const percentage = Math.round((count / total) * 100);
              
              return (
                <div key={type} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg ${config.bgClass}`}>
                      <IconComponent className={`h-4 w-4 ${config.iconClass}`} />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{config.label}</p>
                      <p className="text-xs text-gray-500">{percentage}% of total</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold">{count}</p>
                    <Progress value={percentage} className="w-20 h-2 mt-1" />
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderAccessLevelBreakdown = () => {
    if (!trainingStats.content_by_access_level) return null;
    
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Crown className="h-5 w-5" />
            Content by Access Level
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Object.entries(trainingStats.content_by_access_level).map(([level, count]) => {
              const config = accessLevelConfig[level] || accessLevelConfig.free;
              const IconComponent = config.icon;
              const total = trainingStats.total_content || 1;
              const percentage = Math.round((count / total) * 100);
              
              return (
                <div key={level} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div 
                      className="p-2 rounded-lg"
                      style={{ backgroundColor: `${config.color}20` }}
                    >
                      <IconComponent 
                        className="h-4 w-4" 
                        style={{ color: config.color }}
                      />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{config.label}</p>
                      <p className="text-xs text-gray-500">{percentage}% of total</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold">{count}</p>
                    <Progress value={percentage} className="w-20 h-2 mt-1" />
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Settings className="h-8 w-8 text-blue-600" />
            Training Vault Management
          </h1>
          <p className="text-gray-600 mt-2">
            Manage Boot Camp training content and monitor member progress
          </p>
        </div>
        <div className="flex space-x-3">
          <Button 
            onClick={seedTrainingVault}
            disabled={seedingContent}
            className="bg-green-600 hover:bg-green-700"
          >
            {seedingContent ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Seeding...
              </>
            ) : (
              <>
                <Zap className="h-4 w-4 mr-2" />
                Seed Content
              </>
            )}
          </Button>
          
          <Dialog>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700">
                <Plus className="h-4 w-4 mr-2" />
                Add Content
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Create Training Content</DialogTitle>
                <DialogDescription>
                  Add new training content to the Boot Camp vault
                </DialogDescription>
              </DialogHeader>
              <div className="p-4">
                <p className="text-gray-600">
                  Content creation form coming soon...
                </p>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {renderStatsCard(
          'Total Content', 
          trainingStats.total_content || 0, 
          BookOpen, 
          'blue',
          'All training materials'
        )}
        
        {renderStatsCard(
          'Content Types', 
          Object.keys(trainingStats.content_by_type || {}).length, 
          FileText, 
          'green',
          'Different content formats'
        )}
        
        {renderStatsCard(
          'Access Levels', 
          Object.keys(trainingStats.content_by_access_level || {}).length, 
          Crown, 
          'purple',
          'Membership tier levels'
        )}
        
        {renderStatsCard(
          'Free Content', 
          trainingStats.content_by_access_level?.free || 0, 
          Users, 
          'gray',
          'Available to all members'
        )}
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="content">Content Management</TabsTrigger>
          <TabsTrigger value="achievements">Achievement System</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {renderContentTypeBreakdown()}
            {renderAccessLevelBreakdown()}
          </div>
          
          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
              <CardDescription>
                Common administrative tasks for the training vault
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Button variant="outline" className="h-20 flex-col">
                  <Plus className="h-6 w-6 mb-2" />
                  Add New Content
                </Button>
                
                <Button variant="outline" className="h-20 flex-col">
                  <BarChart3 className="h-6 w-6 mb-2" />
                  View Analytics
                </Button>
                
                <Button variant="outline" className="h-20 flex-col">
                  <Settings className="h-6 w-6 mb-2" />
                  Manage Categories
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="content" className="space-y-6">
          <Card>
            <CardContent className="p-12 text-center">
              <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Content Management</h3>
              <p className="text-gray-600 mb-6">
                Detailed content management interface coming soon. For now, you can seed the training vault with sample content.
              </p>
              <Button 
                onClick={seedTrainingVault}
                disabled={seedingContent}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {seedingContent ? 'Seeding Content...' : 'Seed Sample Content'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="achievements" className="space-y-6">
          <Card>
            <CardContent className="p-12 text-center">
              <Trophy className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Achievement System</h3>
              <p className="text-gray-600 mb-6">
                Monitor member achievements and ranking progression. Achievement management interface coming soon.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <Trophy className="h-8 w-8 text-yellow-600 mx-auto mb-2" />
                  <p className="text-sm font-medium">Profile Completion</p>
                  <p className="text-xs text-gray-600">Users who complete their profiles</p>
                </div>
                
                <div className="bg-green-50 p-4 rounded-lg">
                  <Users className="h-8 w-8 text-green-600 mx-auto mb-2" />
                  <p className="text-sm font-medium">Referral Milestones</p>
                  <p className="text-xs text-gray-600">1st, 12th referral achievements</p>
                </div>
                
                <div className="bg-blue-50 p-4 rounded-lg">
                  <CheckCircle className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                  <p className="text-sm font-medium">Training Completion</p>
                  <p className="text-xs text-gray-600">Boot camp and content completion</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminTrainingVault;